<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useNotify } from '@/composables';
import { YardTaskFlags } from '@nova/core';

import {
  isMoveTask,
  isTaskCreated,
  isTaskInProgress,
  isTaskUrgent,
  toggleTag
} from '@satellite/helpers/yardTaskUtils';
import { useYardTaskMutations } from '@satellite/composables/useYardTaskMutations';

import YardTaskCancelConfirmationDialog from './YardTaskCancelConfirmationDialog.vue';
import YardTaskCompleteConfirmationDialog from './YardTaskCompleteConfirmationDialog.vue';

import type { TaskViewData, MoveTaskViewData } from '@nova/core';

type ButtonProps = {
  size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large';
  density?: 'default' | 'comfortable' | 'compact';
  variant?: 'text' | 'flat' | 'elevated' | 'tonal' | 'outlined' | 'plain';
};

const props = withDefaults(
  defineProps<{
    task: TaskViewData | null;
    isLoading?: boolean;
    buttonProps?: ButtonProps;
  }>(),
  {
    isLoading: false,
    buttonProps: () => ({} as ButtonProps)
  }
);

const { t } = useI18n();
const notify = useNotify();

const { updateTask: updateTaskMutation } = useYardTaskMutations();

const showCancelDialog = ref(false);
const showCompleteDialog = ref(false);

const showActionsMenu = computed(() => {
  return props.isLoading || isTaskInProgress(props.task) || isTaskCreated(props.task);
});

const allowCancelTask = computed(() => {
  return (
    !props.isLoading &&
    props.task?.id &&
    (isTaskInProgress(props.task) || isTaskCreated(props.task))
  );
});

const allowToggleUrgentFlag = computed(() => {
  return (
    !props.isLoading &&
    props.task?.id &&
    (isTaskInProgress(props.task) || isTaskCreated(props.task))
  );
});

const allowCompleteTask = computed(() => {
  if (props.isLoading || !props.task?.id || !isTaskInProgress(props.task)) {
    return false;
  }

  if (isMoveTask(props.task)) {
    const task: MoveTaskViewData = props.task;

    return task.assignedTo && task.originSpot && task.destinationSpot && task.assetContainer;
  }

  // TODO: once we enable check tasks, we should evaluate if we should allow completing it here.
  return false;
});

const getButtonProps = (activatorProps: Record<string, any>) => {
  return {
    ...activatorProps,
    ...props.buttonProps
  };
};

const openCancelDialog = () => {
  showCancelDialog.value = true;
};

const openCompleteDialog = () => {
  showCompleteDialog.value = true;
};

const toggleTaskUrgentFlag = async () => {
  const hadTag = isTaskUrgent(props.task);

  try {
    const updatedTags = toggleTag(props.task?.tags, YardTaskFlags.URGENT);

    await updateTaskMutation(props.task?.id, {
      tags: updatedTags
    });

    notify(
      hadTag
        ? t('yard.messages.urgentTagRemovedSuccessfully')
        : t('yard.messages.urgentTagAddedSuccessfully'),
      'success'
    );
  } catch (error) {
    console.error('Failed to toggle task urgent flag:', error);
    notify(
      hadTag ? t('yard.messages.failedToRemoveUrgentTag') : t('yard.messages.failedToAddUrgentTag'),
      'error'
    );
  }
};
</script>

<template>
  <div class="d-flex flex-row">
    <v-menu v-if="showActionsMenu">
      <template #activator="{ props: activatorProps }">
        <v-btn
          data-testid="yard-task-actions-menu"
          variant="text"
          density="comfortable"
          icon="mdi-dots-vertical"
          v-bind="getButtonProps(activatorProps)"
          :aria-label="t('yard.pages.yardTasks.labels.clickToOpenActionMenu')"
          :disabled="isLoading"
          :loading="isLoading"></v-btn>
      </template>
      <v-list slim density="compact">
        <v-list-item :disabled="!allowToggleUrgentFlag" @click="toggleTaskUrgentFlag">
          <template #prepend>
            <v-icon icon="mdi-flag" size="x-small" />
          </template>
          <v-list-item-title>
            {{
              isTaskUrgent(props.task)
                ? t('yard.drawers.editTaskDrawer.labels.removeFlag')
                : t('yard.drawers.editTaskDrawer.labels.addFlag')
            }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item :disabled="!allowCancelTask" @click="openCancelDialog">
          <template #prepend>
            <v-icon icon="mdi-close" size="x-small" />
          </template>
          <v-list-item-title>
            {{ t('yard.drawers.editTaskDrawer.labels.cancel') }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item :disabled="!allowCompleteTask" @click="openCompleteDialog">
          <template #prepend>
            <v-icon icon="mdi-check" size="x-small" />
          </template>
          <v-list-item-title>
            {{ t('yard.drawers.editTaskDrawer.labels.complete') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <yard-task-cancel-confirmation-dialog v-model="showCancelDialog" :task="task" />
    <yard-task-complete-confirmation-dialog v-model="showCompleteDialog" :task="task" />
  </div>
</template>
